/**
 * Web component for Anear action events with JSON payload
 *
 * Usage:
 *   <anear-action payload='{"START_SPARK_POLL":null}'>Start</anear-action>
 *   <anear-action payload='{"PREVIEW_QUESTION":{"index":0}}'>Preview</anear-action>
 *
 * Optional ABR confirm (handled in EventFrame, same attrs as <anear-nav>):
 *   <anear-action
 *     payload='{"REMOVE_SUBMISSION":{"submissionId":"…"}}'
 *     prompt="Remove this tribute?"
 *     stay="Cancel"
 *     exit="Remove"
 *   >Remove</anear-action>
 *
 * The component handles its own click setup internally. It will be initialized
 * by useAppDisplayDomSetup with the onActionClick handler function.
 */
class AnearAction extends HTMLElement {
  constructor() {
    super()
    this._payload = null
    this._onActionClick = null
  }

  static get observedAttributes() {
    return ['payload']
  }

  connectedCallback() {
    // Make the web component behave exactly like a native element with data-anear-action
    // Don't set any display properties - let CSS classes handle it completely
    // The element should be transparent to layout, behaving as if classes were on a native element

    // Read the payload attribute if it exists (attributeChangedCallback might not fire for initial attributes)
    const payloadAttr = this.getAttribute('payload')
    if (payloadAttr !== null && this._payload === null) {
      this._payload = payloadAttr
    }
    // Note: updateClickHandler() will be called again when initialize() is called
    // with the actual handler, so it's safe to call it here even if _onActionClick is null
    this.updateClickHandler()
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'payload') {
      this._payload = newValue
      this.updateClickHandler()
    }
  }

  /**
   * Initialize the component with action handler
   * Called by useAppDisplayDomSetup to provide the click handler function
   * The component then handles all click setup internally
   *
   * This MUST be called after the element is in the DOM for clicks to work
   */
  initialize(onActionClick) {
    if (!onActionClick) {
      console.warn('anear-action: initialize() called without onActionClick handler', this)
      return
    }
    this._onActionClick = onActionClick
    // Force update the click handler now that we have the handler function
    this.updateClickHandler()
  }

  get payload() {
    // Always check the attribute first, then fall back to internal state
    const attrValue = this.getAttribute('payload')
    return attrValue !== null ? attrValue : this._payload
  }

  set payload(value) {
    if (value) {
      this.setAttribute('payload', value)
      this._payload = value
    } else {
      this.removeAttribute('payload')
      this._payload = null
    }
    this.updateClickHandler()
  }

  /**
   * Sets up the click handler entirely within the web component
   * The component manages its own onclick binding - no external setup needed
   */
  updateClickHandler() {
    const payload = this.payload
    const shouldHandleClick = payload && payload !== 'null' && this._onActionClick

    if (shouldHandleClick) {
      // Handle click setup entirely within the web component class
      // The handler function is passed in via initialize(), but the binding
      // and click handling is all done here in the component
      // Use addEventListener to ensure it works with child elements
      // Remove any existing listener first
      if (this._boundClickHandler) {
        this.removeEventListener('click', this._boundClickHandler, false)
      }

      this._boundClickHandler = (e) => {
        // The handler will find this element via e.target.closest('anear-action')
        // and get the payload from the 'payload' attribute
        if (this.getAttribute('aria-disabled') === 'true') {
          // If disabled due to timeout, request prompt from nearest <anear-timeout> (best-effort) and block.
          try {
            this.closest('anear-timeout')?.requestPrompt?.()
          } catch (_e) {
            // ignore
          }
          e.preventDefault?.()
          e.stopPropagation?.()
          return false
        }
        if (this._onActionClick) {
          this._onActionClick(e)
        }
      }

      // Use bubbling phase (default) - this works naturally with child elements
      // The event will bubble up from child to parent, and we'll catch it here
      this.addEventListener('click', this._boundClickHandler)
    } else {
      // Remove click listener if it exists
      if (this._boundClickHandler) {
        this.removeEventListener('click', this._boundClickHandler, false)
        this._boundClickHandler = null
      }
      this.onclick = null
    }
  }
}

// Register the custom element
if (!customElements.get('anear-action')) {
  customElements.define('anear-action', AnearAction)
}

export default AnearAction
